import { useEffect } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom'
import Layout from './components/layout/Layout'
import Login from './pages/login/Login'
import Register from './pages/register/Register'
import Pending from './pages/pending/Pending'
import Dashboard from './pages/dashboard/Dashboard'
import Connections from './pages/connections/Connections'
import Tools from './pages/tools/Tools'
import ToolWizard from './pages/toolWizard/ToolWizard'
import ToastContainer from './components/toastContainer/ToastContainer'
import OAuthLoginPage from './pages/oauthLogin/OAuthLogin'
import Callback from './pages/callback/Callback'
import { useAuthStore } from './store/appStore'
import { UserRole } from './enums/userRole'
import GuideSelect from './pages/connectGuide/GuideSelect'
import ConnectClaude from './pages/connectGuide/ConnectClaude'
import ConnectGPT from './pages/connectGuide/ConnectGPT'
import ConnectCopilot from './pages/connectGuide/ConnectCopilot'
import SapConnection from './pages/connections/sapConnection/SapConnection'
import S3Connection from './pages/connections/s3Connection/S3Connection'
import N8NConnection from './pages/connections/n8nConnection/N8NConnection'
import { useInterval } from './hooks/useInterval'
import PriorityConnection from './pages/connections/priorityConnection/PriorityConnection'
import MicrosoftGraphConnection from './pages/connections/microsoftGraphConnection/MicrosoftGraphConnection'
import GenericConnection from './pages/connections/genericConnection/GenericConnection'
import MssqlConnection from './pages/connections/mssqlConnection/MssqlConnection'
import SapRfcConnection from './pages/connections/sapRfcConnection/SapRfcConnection'
import SettingsTabs from './pages/settingsTabs/SettingsTabs'
import WidgetConfigurator from './features/widgets/pages/widgetConfigurator/WidgetConfigurator'
import ChatHistory from './pages/chatHistory/ChatHistory'
import ChatAnalytics from './pages/chatAnalytics/ChatAnalytics'
import Portal from './features/widgets/pages/portal/Portal'
import WidgetList from './features/widgets/pages/widgets/WidgetList'
import { AUTH_EXPIRED_EVENT } from './lib/authEvents'
import {onLogoutBroadcast} from './lib/authChannel'
import { toast } from './lib/toast'

function AppSetup() {
  const fetchCurrentUser = useAuthStore(state => state.fetchCurrentUser);

  useEffect(() => {
    void fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    const syncWidgetAuthToWeb = () => { void fetchCurrentUser(); };
    const handleAuthExpired = () => {
      useAuthStore.setState({ user: null, isAuthenticated: false, loading: false });
      toast.clear();
      if (window.location.pathname.startsWith('/portal')) return;
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    };
    window.addEventListener('mcp-chat:authenticated', syncWidgetAuthToWeb as EventListener);
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired as EventListener);
    const unsubscribeLogout = onLogoutBroadcast(handleAuthExpired);
    return () => {
      window.removeEventListener('mcp-chat:authenticated', syncWidgetAuthToWeb as EventListener);
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired as EventListener);
      unsubscribeLogout();
    };
  }, [fetchCurrentUser]);

  useInterval(async () => {
    try {
      await fetchCurrentUser();
    } catch (error: any) {
      if (error.message.includes('401')) {
        void useAuthStore.getState().logout();
      }
    }
  }, 30 * 60 * 1000);

  return (
    <>
      <Outlet />
      <ToastContainer />
    </>
  );
}

function PublicLayout() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const loading = useAuthStore(state => state.loading);
  const user = useAuthStore(state => state.user);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#6b7280' }}>
        Loading...
      </div>
    );
  }
  if (isAuthenticated) {
    if (user?.role?.id === UserRole.Pending) return <Navigate to="/pending" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function ProtectedLayout() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const loading = useAuthStore(state => state.loading);
  const user = useAuthStore(state => state.user);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#6b7280' }}>
        Loading...
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role?.id === UserRole.Pending) return <Navigate to="/pending" replace />;

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppSetup />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: 'login/:error?', element: <Login /> },
          { path: 'register', element: <Register /> },
        ],
      },
      { path: 'authorize', element: <OAuthLoginPage /> },
      { path: 'callback', element: <Callback /> },
      { path: 'portal/:widgetId?', element: <Portal /> },
      { path: 'pending', element: <Pending /> },
      {
        element: <ProtectedLayout />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <Dashboard /> },
          { path: 'connectors', element: <Connections /> },
          { path: 'connectors/sap/:configId', element: <SapConnection /> },
          { path: 'connectors/s3/:configId', element: <S3Connection /> },
          { path: 'connectors/priority/:configId', element: <PriorityConnection /> },
          { path: 'connectors/n8n/:configId', element: <N8NConnection /> },
          { path: 'connectors/generic/:configId', element: <GenericConnection /> },
          { path: 'connectors/microsoft-graph/:configId', element: <MicrosoftGraphConnection /> },
          { path: 'connectors/mssql/:configId', element: <MssqlConnection /> },
          { path: 'connectors/sap-rfc/:configId', element: <SapRfcConnection /> },
          { path: 'toolslist', element: <Tools /> },
          { path: 'toolslist/new', element: <ToolWizard /> },
          { path: 'toolslist/:id', element: <ToolWizard /> },
          {
            path: 'connect-guide',
            element: <GuideSelect />,
            children: [
              { path: 'claude', element: <ConnectClaude /> },
              { path: 'copilot', element: <ConnectCopilot /> },
              { path: 'gpt', element: <ConnectGPT /> },
            ],
          },
          { path: 'widgets', element: <WidgetList /> },
          { path: 'widgets/new', element: <WidgetConfigurator /> },
          { path: 'widgets/:id', element: <WidgetConfigurator /> },
          { path: 'widget-configurator', element: <Navigate to="/widgets" replace /> },
          { path: 'chat-analytics', element: <ChatAnalytics /> },
          { path: 'chat-history', element: <ChatHistory /> },
          { path: 'settings', element: <SettingsTabs /> },
        ],
      },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App
